// ============================================================================
// 튜닝 패널 — lil-gui. 프리셋 8슬롯(localStorage), JSON 복사/붙여넣기, A/B(Space), URL 공유, 숨김(H).
// ============================================================================
import GUI from 'lil-gui';
import { P, DEFAULT_PARAMS, RANGES, Params, cloneParams, assignParams } from './params';
import { audio } from './audio';
import { haptics } from './haptics';

const LABELS: Record<string, string> = {
  world: '크기',
  draw: '뽑기 확률 (리셋 후 반영)',
  cut: '자르기',
  open: '개봉',
  flip: '뒤집기',
  swipe: '넘기기',
  reveal: 'AR·UR 등장 연출',
  binder: '바인더',
  econ: '경제 (가격을 바꾸면 tools/econ-stats.mjs)',
  input: '입력',
  sound: '소리',
  haptic: '진동',
};

export interface PanelHost {
  reset(): void;
  toggleDiag(force?: boolean): void;
  setGraphics(on: boolean): void;
  onParamsApplied(): void;
  // 바인더 저장소 조작 (개발용)
  binderSave(): unknown;
  binderApply(json: string): void;
  binderClear(): void;
  binderFill(): void;
}

interface Slot {
  name: string;
  params: Params;
}

const SLOTS = 8;
const LS_PRESETS = 'cr.presets.v1';
const LS_AB = 'cr.ab.v1';

const BUILTIN = import.meta.glob('../presets/*.json', { eager: true }) as Record<string, { default: Partial<Params> & { name?: string } }>;

export function paramsDiff(cur: any, def: any): any {
  const out: any = {};
  for (const k of Object.keys(cur)) {
    const c = cur[k], d = def[k];
    if (c && typeof c === 'object') {
      const sub = paramsDiff(c, d ?? {});
      if (Object.keys(sub).length) out[k] = sub;
    } else if (c !== d) out[k] = c;
  }
  return out;
}

function b64url(s: string): string {
  return btoa(unescape(encodeURIComponent(s))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
export function unb64url(s: string): string {
  s = s.replace(/-/g, '+').replace(/_/g, '/');
  while (s.length % 4) s += '=';
  return decodeURIComponent(escape(atob(s)));
}

export class Panel {
  gui: GUI;
  visible = true;
  private slots: (Slot | null)[] = [];
  private ab: { A: Params | null; B: Params | null; active: '' | 'A' | 'B' } = { A: null, B: null, active: '' };
  private presetState = { slot: 1, name: '' };
  private diagState = { diag: false, mute: false, graphics: true, haptic: true };
  private slotCtrl: any;
  private jsonBox: HTMLElement;
  private textarea: HTMLTextAreaElement;
  private badge: HTMLElement;
  private toast: HTMLElement;
  private badgeTimer = 0;
  private toastTimer = 0;
  private abLabel = { text: 'A/B: 비어 있음' };

  constructor(private host: PanelHost) {
    this.gui = new GUI({ title: 'CARD RIPPER — 튜닝' });
    this.jsonBox = document.getElementById('json')!;
    this.textarea = document.createElement('textarea');
    this.textarea.spellcheck = false;
    this.badge = document.getElementById('ab')!;
    this.toast = document.getElementById('toast')!;
    this.loadSlots();
    this.loadAB();
    this.build();
    this.buildJsonBox();
  }

  // ---------------------------------------------------------------- 빌드
  private addGroup(folder: GUI, obj: any, prefix: string): void {
    for (const k of Object.keys(obj)) {
      const v = obj[k];
      const key = prefix + k;
      if (v && typeof v === 'object') {
        const sub = folder.addFolder(k);
        sub.close();
        this.addGroup(sub, v, key + '.');
      } else if (typeof v === 'boolean') {
        folder.add(obj, k).onChange(() => this.onChanged(key));
      } else {
        const r = RANGES[key];
        const c = r ? folder.add(obj, k, r[0], r[1], r[2]) : folder.add(obj, k);
        c.onChange(() => this.onChanged(key));
      }
    }
  }

  private build(): void {
    const g = this.gui;
    for (const grp of ['world', 'draw', 'cut', 'open', 'flip', 'swipe', 'reveal', 'binder', 'econ', 'input', 'sound', 'haptic'] as const) {
      const f = g.addFolder(LABELS[grp]);
      f.close();
      this.addGroup(f, (P as any)[grp], grp + '.');
    }

    // ---- 프리셋
    const pf = g.addFolder('프리셋');
    this.slotCtrl = pf.add(this.presetState, 'slot', this.slotOptions()).name('슬롯');
    this.slotCtrl.onChange((v: number) => {
      const s = this.slots[v - 1];
      this.presetState.name = s ? s.name : '';
      this.refresh();
    });
    pf.add(this.presetState, 'name').name('이름');
    pf.add({ save: () => this.saveSlot() }, 'save').name('현재 값을 슬롯에 저장');
    pf.add({ load: () => this.loadSlot() }, 'load').name('슬롯 불러오기');
    pf.add({ del: () => this.deleteSlot() }, 'del').name('슬롯 삭제');
    const builtinNames = Object.keys(BUILTIN).map((p) => p.split('/').pop()!.replace('.json', ''));
    if (builtinNames.length) {
      const bi = { pick: builtinNames[0] };
      pf.add(bi, 'pick', builtinNames).name('내장 프리셋');
      pf.add({ load: () => this.loadBuiltin(bi.pick) }, 'load').name('내장 프리셋 적용');
    }
    pf.add({ reset: () => this.applyParams(DEFAULT_PARAMS, '기본값') }, 'reset').name('기본값으로');

    // ---- A/B
    const af = g.addFolder('A/B 비교 (Space)');
    af.add({ a: () => this.setAB('A') }, 'a').name('A ← 현재 값');
    af.add({ b: () => this.setAB('B') }, 'b').name('B ← 현재 값');
    af.add({ t: () => this.toggleAB() }, 't').name('A/B 전환 (Space)');
    af.add(this.abLabel, 'text').name('상태').disable();
    this.updateABLabel();

    // ---- 공유
    const sf = g.addFolder('JSON / URL');
    sf.add({ c: () => this.copyJson() }, 'c').name('JSON 복사 (클립보드 + 아래 상자)');
    sf.add({ p: () => this.pasteJson() }, 'p').name('아래 상자의 JSON 적용');
    sf.add({ u: () => this.copyUrl() }, 'u').name('현재 설정 URL 복사');

    // ---- 진단
    const df = g.addFolder('진단 / 판정 모드');
    df.add(this.diagState, 'diag').name('진단 표시 (D)').onChange((v: boolean) => this.host.toggleDiag(v)).listen();
    df.add(this.diagState, 'mute').name('소리 끄기').onChange((v: boolean) => {
      audio.muted = v;
      audio.setMaster();
    });
    df.add(this.diagState, 'graphics').name('그래픽 (끄면 소리만)').onChange((v: boolean) => this.host.setGraphics(v));
    this.diagState.haptic = haptics.enabled;
    df.add(this.diagState, 'haptic').name('진동').onChange((v: boolean) => (haptics.enabled = v));
    df.add({ r: () => this.host.reset() }, 'r').name('리셋 (R)');

    // ---- 바인더 저장소 (개발용). 리셋(R)은 저장소를 절대 건드리지 않는다.
    const bf = g.addFolder('바인더 저장소');
    bf.close();
    bf.add({ c: () => { this.host.binderClear(); this.showToast('바인더를 비웠다'); } }, 'c').name('바인더 비우기');
    bf.add({ c: () => { void navigator.clipboard?.writeText(JSON.stringify(this.host.binderSave())); this.showToast('저장소 JSON 복사됨'); } }, 'c').name('JSON 복사');
    bf.add({ c: () => { const t = prompt('바인더 저장소 JSON'); if (t) { this.host.binderApply(t); this.showToast('저장소 적용됨'); } } }, 'c').name('JSON 적용');
    bf.add({ c: () => { this.host.binderFill(); this.showToast('테스트로 채웠다'); } }, 'c').name('테스트 채우기');

    // ---- 진동 자가진단 (기기별로 되고 안 되고가 갈려서 폰에서 바로 확인해야 한다)
    const hf = g.addFolder('진동 자가진단');
    const ro = (obj: object, key: string, get: () => string) =>
      Object.defineProperty(obj, key, { get, set: () => {}, configurable: true, enumerable: true });
    const hs = {} as { status: string; verdict: string; ua: string };
    ro(hs, 'status', () => haptics.status());
    ro(hs, 'verdict', () => haptics.verdict());
    ro(hs, 'ua', () => haptics.ua);
    hf.add(hs, 'status').name('상태').disable().listen();
    hf.add(hs, 'verdict').name('원인 추정').disable().listen();
    hf.add(hs, 'ua').name('브라우저 UA').disable();
    for (const ms of [10, 30, 100, 400]) {
      hf.add({ t: () => this.testHaptic(ms, `${ms}ms`) }, 't').name(`${ms}ms 쏘기`);
    }
    hf.add({ t: () => this.testHaptic([150, 100, 150, 100, 300], '긴 패턴') }, 't').name('긴 패턴 쏘기');
    hf.add({ t: () => haptics.stop() }, 't').name('진동 중지');
  }

  /** 자가진단 버튼 — enabled 와 무관하게 무조건 쏜다 */
  private testHaptic(v: number | number[], label: string): void {
    const ok = haptics.test(v);
    this.refresh();
    this.showToast(ok ? `${label} 호출 수락됨 (true)` : `${label} 호출 거부됨 (false)`);
  }

  private buildJsonBox(): void {
    const box = this.jsonBox;
    box.style.display = 'flex';
    box.appendChild(this.textarea);
    const row = document.createElement('div');
    row.className = 'row';
    const mk = (label: string, fn: () => void) => {
      const b = document.createElement('button');
      b.textContent = label;
      b.addEventListener('click', fn);
      row.appendChild(b);
    };
    mk('JSON 복사', () => this.copyJson());
    mk('JSON 적용', () => this.pasteJson());
    mk('URL 복사', () => this.copyUrl());
    mk('전체 선택', () => {
      this.textarea.focus();
      this.textarea.select();
    });
    box.appendChild(row);
  }

  // ---------------------------------------------------------------- 공용
  private onChanged(key: string): void {
    if (key.startsWith('sound.master')) audio.setMaster();
    this.ab.active = '';
    this.updateABLabel();
    this.host.onParamsApplied();
  }

  refresh(): void {
    this.gui.controllersRecursive().forEach((c) => c.updateDisplay());
  }

  applyParams(src: Partial<Params>, label = ''): void {
    assignParams(P, src);
    audio.setMaster();
    this.refresh();
    this.host.onParamsApplied();
    if (label) this.showToast(`적용: ${label}`);
  }

  showToast(msg: string): void {
    this.toast.textContent = msg;
    this.toast.style.opacity = '1';
    clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => (this.toast.style.opacity = '0'), 1400);
  }

  // ---------------------------------------------------------------- 슬롯
  private slotOptions(): Record<string, number> {
    const o: Record<string, number> = {};
    for (let i = 0; i < SLOTS; i++) {
      const s = this.slots[i];
      o[`${i + 1}. ${s ? s.name || '(이름 없음)' : '— 비어 있음'}`] = i + 1;
    }
    return o;
  }
  private loadSlots(): void {
    try {
      const raw = localStorage.getItem(LS_PRESETS);
      const arr = raw ? (JSON.parse(raw) as (Slot | null)[]) : [];
      this.slots = Array.from({ length: SLOTS }, (_, i) => arr[i] ?? null);
    } catch {
      this.slots = Array.from({ length: SLOTS }, () => null);
    }
  }
  private persistSlots(): void {
    try {
      localStorage.setItem(LS_PRESETS, JSON.stringify(this.slots));
    } catch {
      /* ignore */
    }
    this.slotCtrl.options(this.slotOptions());
    this.slotCtrl.onChange((v: number) => {
      const s = this.slots[v - 1];
      this.presetState.name = s ? s.name : '';
      this.refresh();
    });
    this.refresh();
  }
  private saveSlot(): void {
    const i = this.presetState.slot - 1;
    const name = this.presetState.name.trim() || `preset ${i + 1}`;
    this.slots[i] = { name, params: cloneParams(P) };
    this.presetState.name = name;
    this.persistSlots();
    this.showToast(`저장: 슬롯 ${i + 1} "${name}"`);
  }
  private loadSlot(): void {
    const i = this.presetState.slot - 1;
    const s = this.slots[i];
    if (!s) return this.showToast(`슬롯 ${i + 1} 비어 있음`);
    this.applyParams(s.params, `슬롯 ${i + 1} "${s.name}"`);
  }
  private deleteSlot(): void {
    const i = this.presetState.slot - 1;
    this.slots[i] = null;
    this.presetState.name = '';
    this.persistSlots();
    this.showToast(`삭제: 슬롯 ${i + 1}`);
  }
  private loadBuiltin(name: string): void {
    const entry = Object.entries(BUILTIN).find(([p]) => p.endsWith(`/${name}.json`));
    if (!entry) return;
    const merged = cloneParams(DEFAULT_PARAMS);
    assignParams(merged, entry[1].default);
    this.applyParams(merged, `내장 "${name}"`);
  }

  // ---------------------------------------------------------------- A/B
  private loadAB(): void {
    try {
      const raw = localStorage.getItem(LS_AB);
      if (raw) {
        const o = JSON.parse(raw);
        this.ab.A = o.A ?? null;
        this.ab.B = o.B ?? null;
      }
    } catch {
      /* ignore */
    }
  }
  private persistAB(): void {
    try {
      localStorage.setItem(LS_AB, JSON.stringify({ A: this.ab.A, B: this.ab.B }));
    } catch {
      /* ignore */
    }
  }
  private setAB(which: 'A' | 'B'): void {
    this.ab[which] = cloneParams(P);
    this.ab.active = which;
    this.persistAB();
    this.updateABLabel();
    this.showToast(`${which} ← 현재 값`);
  }
  toggleAB(): void {
    if (!this.ab.A || !this.ab.B) {
      this.showToast('A/B 둘 다 먼저 저장하세요');
      return;
    }
    const next = this.ab.active === 'A' ? 'B' : 'A';
    assignParams(P, this.ab[next]!);
    audio.setMaster();
    this.ab.active = next;
    this.refresh();
    this.updateABLabel();
    this.host.onParamsApplied();
    this.badge.textContent = next;
    this.badge.style.opacity = '1';
    clearTimeout(this.badgeTimer);
    this.badgeTimer = window.setTimeout(() => (this.badge.style.opacity = '0'), 600);
  }
  private updateABLabel(): void {
    const a = this.ab.A ? 'A✓' : 'A–';
    const b = this.ab.B ? 'B✓' : 'B–';
    this.abLabel.text = `${a} ${b}  현재: ${this.ab.active || '(수정됨)'}`;
    this.refresh();
  }

  // ---------------------------------------------------------------- JSON / URL
  private async clip(text: string): Promise<boolean> {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      return false;
    }
  }
  private copyJson(): void {
    const s = JSON.stringify(P, null, 2);
    this.textarea.value = s;
    void this.clip(s).then((ok) => this.showToast(ok ? 'JSON 클립보드에 복사됨' : 'JSON 아래 상자에 표시됨 (클립보드 불가)'));
  }
  private pasteJson(): void {
    try {
      const obj = JSON.parse(this.textarea.value);
      this.applyParams(obj, 'JSON');
    } catch (e) {
      this.showToast('JSON 파싱 실패');
    }
  }
  currentUrl(): string {
    const u = new URL(location.href);
    const diff = paramsDiff(P, DEFAULT_PARAMS);
    if (Object.keys(diff).length) u.searchParams.set('p', b64url(JSON.stringify(diff)));
    else u.searchParams.delete('p');
    return u.toString();
  }
  private copyUrl(): void {
    const url = this.currentUrl();
    this.textarea.value = url;
    void this.clip(url).then((ok) => this.showToast(ok ? 'URL 복사됨' : 'URL 아래 상자에 표시됨'));
  }

  // ---------------------------------------------------------------- 표시
  toggle(force?: boolean): void {
    this.visible = force ?? !this.visible;
    if (this.visible) this.gui.show();
    else this.gui.hide();
    this.jsonBox.style.display = this.visible ? 'flex' : 'none';
  }
  setDiagChecked(v: boolean): void {
    this.diagState.diag = v;
  }
}
